import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Clock,
  FolderKanban,
  Loader2,
  Package,
  Pencil,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { EntityTableTab } from "@/components/shared/EntityTableTab";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { TimelineTab } from "@/components/shared/TimelineTab";
import { PhasesTab } from "../components/PhasesTab";
import { formatDate, formatDateTime } from "@/lib/utils";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useProject,
  useProjectSubEntity,
  useDeleteProject,
  useUpdateProject,
} from "../hooks/useProjects";
import type { ProjectDetail, ProjectStatus } from "../types";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { NewTaskDrawer } from "@/modules/tasks/components/NewTaskDrawer";
import { NewActionDrawer } from "@/modules/actions/components/NewActionDrawer";
import { NewMeetingDrawer } from "@/modules/meetings/components/NewMeetingDrawer";
import { NewMilestoneDrawer } from "@/modules/milestones/components/NewMilestoneDrawer";
import { ExternalCompanyPicker } from "@/components/shared/ExternalCompanyPicker";
import { EntityDocumentsTab } from "@/modules/media/components/EntityDocumentsTab";
import { BudgetsTabContent } from "@/modules/budgets/components/BudgetsTabContent";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import { NotesTab } from "@/modules/notes/components/NotesTab";
import { IdeasTab } from "@/modules/ideas/components/IdeasTab";
import { DecisionsTab } from "@/modules/decisions/components/DecisionsTab";
import { EntityTeamTab } from "@/modules/teams/components/EntityTeamTab";
import { useTeam } from "@/modules/teams/hooks/useTeam";

type Tab =
  | "overview"
  | "phases"
  | "meetings"
  | "tasks"
  | "actions"
  | "notes"
  | "ideas"
  | "milestones"
  | "decisions"
  | "budgets"
  | "expenses"
  | "team"
  | "documents"
  | "timeline";

const TABS: { key: Tab; label: string; counted?: boolean }[] = [
  { key: "overview", label: "Overview" },
  { key: "phases", label: "Phases" },
  { key: "meetings", label: "Meetings", counted: true },
  { key: "tasks", label: "Tasks", counted: true },
  { key: "actions", label: "Actions", counted: true },
  { key: "notes", label: "Notes" },
  { key: "ideas", label: "Ideas" },
  { key: "milestones", label: "Milestones" },
  { key: "decisions", label: "Decisions" },
  { key: "budgets", label: "Budgets" },
  { key: "expenses", label: "Expenses" },
  { key: "team", label: "Team", counted: true },
  { key: "documents", label: "Documents" },
  { key: "timeline", label: "Timeline" },
];

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    PLANNING: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Planning" },
    ACTIVE: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
    ON_HOLD: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "On Hold" },
    COMPLETED: { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)", label: "Completed" },
    CANCELLED: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Cancelled" },
  };
  const s = styles[status] ?? styles.PLANNING;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

const TYPE_BADGE_COLORS: Record<string, { bg: string; fg: string }> = {
  poc: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  pilot: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
  production: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  internal: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};
const NEUTRAL_TYPE_BADGE = { bg: "hsl(210,30%,94%)", fg: "hsl(215,20%,35%)" };

function TypeBadge({
  type,
  label,
}: {
  type: string | null;
  label?: string;
}) {
  if (!type) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const c = TYPE_BADGE_COLORS[type] ?? NEUTRAL_TYPE_BADGE;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.fg }}
    >
      {label ?? type}
    </span>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}

export default function ProjectDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, isError } = useProject(id);
  const deleteProject = useDeleteProject();
  const updateProject = useUpdateProject(id);

  const [tab, setTab] = useState<Tab>("overview");
  const [editMode, setEditMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
  const [milestoneDrawerOpen, setMilestoneDrawerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Sub-entity queries (only fetched when tab is active)
  const meetings = useProjectSubEntity<any>(id, "meetings", tab === "meetings" ? { page: 1, limit: 50 } : {});
  const tasks = useProjectSubEntity<any>(id, "tasks", tab === "tasks" ? { page: 1, limit: 50 } : {});
  const actions = useProjectSubEntity<any>(id, "actions", tab === "actions" ? { page: 1, limit: 50 } : {});
  const milestones = useProjectSubEntity<any>(id, "milestones", tab === "milestones" ? { page: 1, limit: 50 } : {});
  const teamQuery = useTeam("PROJECT", id);
  const teamCount = teamQuery.data?.length ?? 0;

  // project_type dropdown is shared by the header badge, the Overview tab,
  // and the audit-history badge below.
  const { data: headerProjectTypes = [] } = useCategoryItems("project_type");
  const headerTypeLabel = project?.type
    ? headerProjectTypes.find((t) => t.value === project.type)?.label
    : undefined;

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          <span className="text-sm text-muted-foreground">
            Loading project…
          </span>
        </div>
      </AppLayout>
    );
  }

  if (isError || !project) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-destructive">
          Failed to load project.
        </div>
      </AppLayout>
    );
  }

  function handleDelete() {
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    await deleteProject.mutateAsync(id);
    navigate("/projects");
  }

  const tabCount = (key: Tab): string | undefined => {
    if (key === "meetings") return String(project._count.meetings);
    if (key === "tasks") return String(project._count.tasks);
    if (key === "actions") return String(project._count.actions);
    if (key === "team") return String(teamCount);
    return undefined;
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Projects", href: "/projects" },
        { label: project.name },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Toast */}
        {toast && (
          <div className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg" style={{ borderColor: "hsl(142,55%,80%)" }}>
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/projects")}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <FolderKanban className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">
              View Project
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="History"
              onClick={() => setHistoryOpen(true)}
            >
              <Clock className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Edit"
              onClick={() => {
                setEditMode((v) => !v);
                if (editMode) setTab("overview");
              }}
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Delete"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Project header card */}
        <Card className="w-full">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1">
                <h2
                  className="font-bold tracking-tight"
                  style={{ fontSize: 20, fontWeight: 700 }}
                >
                  {project.name}
                </h2>
                <div className="mt-1.5 flex flex-wrap items-center gap-3">
                  <ReferenceIdBadge id={project.code} />
                  <TypeBadge type={project.type} label={headerTypeLabel} />
                  <StatusBadge status={project.status} />
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  {project.company && (
                    <a
                      href={`/companies/${project.company.id}`}
                      className="flex items-center gap-1 text-primary hover:underline"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/companies/${project.company!.id}`);
                      }}
                    >
                      <Building2 className="h-3 w-3" strokeWidth={1.5} />
                      {project.company.name}
                    </a>
                  )}
                  {project.product && (
                    <>
                      <span className="text-muted-foreground">›</span>
                      <a
                        href={`/products/${project.product.id}`}
                        className="flex items-center gap-1 text-primary hover:underline"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/products/${project.product!.id}`);
                        }}
                      >
                        <Package className="h-3 w-3" strokeWidth={1.5} />
                        {project.product.name}
                      </a>
                    </>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-4">
                  {project.manager && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-[8px] font-semibold text-[hsl(210,100%,28%)]">
                        {project.manager.name.charAt(0).toUpperCase()}
                      </div>
                      {project.manager.name}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-24 rounded-full bg-[hsl(210,16%,93%)]">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${Math.min(100, project.progress)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {project.progress}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab bar */}
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4 overflow-x-auto">
            {TABS.map((t) => {
              const count = tabCount(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                  {t.counted && count && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      ({count})
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        {tab === "overview" && (
          <OverviewTab
            project={project}
            editMode={editMode}
            onSave={async (payload) => {
              await updateProject.mutateAsync(payload);
              setEditMode(false);
              showToast("Project updated successfully");
            }}
            onCancel={() => setEditMode(false)}
            isSaving={updateProject.isPending}
          />
        )}
        {tab === "phases" && id && (
          <PhasesTab
            projectId={id}
            onTabChange={(t) => setTab(t as Tab)}
          />
        )}
        {tab === "meetings" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setMeetingDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Meeting
              </Button>
            </div>
            <EntityTableTab
              data={meetings.data?.data ?? []}
              isLoading={meetings.isLoading && !meetings.data}
              columns={["title", "scheduledAt", "organizer", "status"]}
              emptyText="No meetings found"
              onRowClick={(row) => navigate(`/meetings/${row.id}`)}
            />
          </div>
        )}
        {tab === "tasks" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setTaskDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Task
              </Button>
            </div>
            <EntityTableTab
              data={tasks.data?.data ?? []}
              isLoading={tasks.isLoading && !tasks.data}
              columns={["title", "priority", "status", "dueDate", "assignee"]}
              emptyText="No tasks found"
              onRowClick={(row) => navigate(`/tasks/${row.id}`)}
            />
          </div>
        )}
        {tab === "actions" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setActionDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Action
              </Button>
            </div>
            <EntityTableTab
              data={actions.data?.data ?? []}
              isLoading={actions.isLoading && !actions.data}
              columns={["title", "priority", "source", "status", "dueDate", "assignee"]}
              emptyText="No actions found"
              onRowClick={(row) => navigate(`/actions/${row.id}`)}
            />
          </div>
        )}
        {tab === "notes" && (
          <NotesTab
            filter={{ projectId: project.id }}
            prefill={{
              projectId: project.id,
              companyId: project.companyId,
              productId: project.productId,
              locked: { projectId: true },
            }}
          />
        )}
        {tab === "milestones" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setMilestoneDrawerOpen(true)}>
                <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
                New Milestone
              </Button>
            </div>
            <EntityTableTab
              data={milestones.data?.data ?? []}
              isLoading={milestones.isLoading && !milestones.data}
              columns={["title", "status", "progress", "dueDate"]}
              emptyText="No milestones found"
              onRowClick={(row) => navigate(`/milestones/${row.id}`)}
            />
          </div>
        )}
        {tab === "ideas" && (
          <IdeasTab
            filter={{ projectId: project.id }}
            prefill={{
              projectId: project.id,
              companyId: project.companyId,
              productId: project.productId,
              locked: { projectId: true },
            }}
          />
        )}
        {tab === "decisions" && (
          <DecisionsTab
            filter={{ projectId: project.id }}
            prefill={{
              projectId: project.id,
              companyId: project.companyId,
              productId: project.productId,
            }}
          />
        )}
        {tab === "budgets" && (
          <BudgetsTabContent projectId={project.id} />
        )}
        {tab === "expenses" && (
          <ExpensesTabContent projectId={project.id} />
        )}
        {tab === "team" && (
          <EntityTeamTab entityType="PROJECT" entityId={project.id} />
        )}
        {tab === "documents" && (
          <EntityDocumentsTab
            entityType="PROJECT"
            entityId={project.id}
            entityLabel={project.name}
            defaultView="grid"
            hideTree
          />
        )}
        {tab === "timeline" && <TimelineTab entityType="project" entityId={id} />}

        {/* Drawers */}
        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="project"
          entityId={project.id}
          entityLabel={project.name}
        />
        <NewTaskDrawer
          isOpen={taskDrawerOpen}
          onClose={() => setTaskDrawerOpen(false)}
          prefill={{
            projectId: project.id,
            companyId: project.companyId,
            productId: project.productId,
            locked: { projectId: true },
          }}
          onCreated={() => setToast("Task created")}
        />
        <NewActionDrawer
          isOpen={actionDrawerOpen}
          onClose={() => setActionDrawerOpen(false)}
          prefill={{
            projectId: project.id,
            companyId: project.companyId,
            productId: project.productId,
            locked: {
              projectId: true,
              companyId: !!project.companyId,
              productId: !!project.productId,
            },
          }}
          onCreated={() => setToast("Action created")}
        />
        <NewMeetingDrawer
          isOpen={meetingDrawerOpen}
          onClose={() => setMeetingDrawerOpen(false)}
          onCreated={(code) => showToast(`Meeting created: ${code}`)}
        />
        <NewMilestoneDrawer
          isOpen={milestoneDrawerOpen}
          onClose={() => setMilestoneDrawerOpen(false)}
          prefill={{
            projectId: project.id,
            companyId: project.companyId,
            productId: project.productId,
            locked: { projectId: true },
          }}
          onCreated={() => setToast("Milestone created")}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete project?"
          description={`Are you sure you want to delete '${project.name}'? It will be removed from your Projects list.`}
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}

// ── Overview Tab with Edit Mode ──────────────────────────
type EditFormState = {
  name: string;
  type: string;
  status: ProjectStatus;
  description: string;
  startDate: string;
  endDate: string;
  progress: number;
  managerId: string;
  clientCompanyId: string;
};

function initForm(p: ProjectDetail): EditFormState {
  return {
    name: p.name,
    type: p.type ?? "",
    status: p.status,
    description: p.description ?? "",
    startDate: p.startDate ? p.startDate.slice(0, 10) : "",
    endDate: p.endDate ? p.endDate.slice(0, 10) : "",
    progress: p.progress,
    managerId: p.managerId ?? "",
    clientCompanyId: p.clientCompanyId ?? "",
  };
}

function OverviewTab({
  project,
  editMode,
  onSave,
  onCancel,
  isSaving,
}: {
  project: ProjectDetail;
  editMode: boolean;
  onSave: (payload: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState<EditFormState>(() => initForm(project));

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const { data: projectTypeOptions = [] } = useCategoryItems("project_type");
  const activeTypeOptions = projectTypeOptions.filter(
    (t) => t.isActive || t.value === form.type,
  );
  const typeLabel = projectTypeOptions.find(
    (t) => t.value === project.type,
  )?.label;

  // Reset form when project data changes or edit mode toggles
  useEffect(() => {
    setForm(initForm(project));
  }, [project, editMode]);

  function set<K extends keyof EditFormState>(field: K, value: EditFormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    await onSave({
      name: form.name.trim(),
      type: form.type || null,
      status: form.status,
      description: form.description.trim() || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      progress: form.progress,
      managerId: form.managerId || null,
      clientCompanyId: form.clientCompanyId || null,
    });
  }

  return (
    <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-10">
      {/* Left 70% */}
      <div className="space-y-6 lg:col-span-7">
        <Card>
          <CardContent className="space-y-5 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Project Information
            </h3>
            {editMode ? (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">
                      Project Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="edit-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Project Code</Label>
                    <Input value={project.code} disabled className="bg-muted font-mono" />
                    <p className="text-[11px] text-muted-foreground">
                      Code is auto-generated and immutable.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-type">Type</Label>
                    <Select
                      id="edit-type"
                      value={form.type}
                      onChange={(e) => set("type", e.target.value)}
                    >
                      <option value="">— Select type —</option>
                      {activeTypeOptions.map((t) => (
                        <option key={t.id} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-status">Status</Label>
                    <Select
                      id="edit-status"
                      value={form.status}
                      onChange={(e) => set("status", e.target.value as ProjectStatus)}
                    >
                      <option value="PLANNING">Planning</option>
                      <option value="ACTIVE">Active</option>
                      <option value="ON_HOLD">On Hold</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="CANCELLED">Cancelled</option>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <textarea
                    id="edit-description"
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    rows={4}
                    className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Project description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-start-date">Start Date</Label>
                    <Input
                      id="edit-start-date"
                      type="date"
                      value={form.startDate}
                      onChange={(e) => set("startDate", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-end-date">End Date</Label>
                    <Input
                      id="edit-end-date"
                      type="date"
                      value={form.endDate}
                      onChange={(e) => set("endDate", e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-progress">Progress ({form.progress}%)</Label>
                  <input
                    id="edit-progress"
                    type="range"
                    min={0}
                    max={100}
                    value={form.progress}
                    onChange={(e) => set("progress", Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-manager">Manager</Label>
                  <select
                    id="edit-manager"
                    value={form.managerId}
                    onChange={(e) => set("managerId", e.target.value)}
                    className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No manager</option>
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <FieldRow label="Project Name" value={project.name} />
                  <FieldRow label="Project Code" value={project.code} />
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Type
                    </div>
                    <TypeBadge type={project.type} label={typeLabel} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Status
                    </div>
                    <StatusBadge status={project.status} />
                  </div>
                </div>
                <FieldRow label="Description" value={project.description} />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <FieldRow label="Start Date" value={project.startDate ? formatDate(project.startDate) : null} />
                  <FieldRow label="End Date" value={project.endDate ? formatDate(project.endDate) : null} />
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Progress
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 rounded-full bg-[hsl(210,16%,93%)]">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.min(100, project.progress)}%` }}
                        />
                      </div>
                      <span className="text-sm text-foreground">{project.progress}%</span>
                    </div>
                  </div>
                  <FieldRow label="Manager" value={project.manager?.name} />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Association Card */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Company
                </div>
                {project.company ? (
                  <a
                    href={`/companies/${project.company.id}`}
                    className="text-sm font-medium text-primary hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/companies/${project.company!.id}`);
                    }}
                  >
                    {project.company.name}
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Product
                </div>
                {project.product ? (
                  <a
                    href={`/products/${project.product.id}`}
                    className="text-sm font-medium text-primary hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/products/${project.product!.id}`);
                    }}
                  >
                    {project.product.name}
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Client (External)
                </div>
                {editMode ? (
                  <ExternalCompanyPicker
                    id="edit-client-company"
                    value={form.clientCompanyId}
                    onChange={(v) => set("clientCompanyId", v)}
                    systemCodes={["client", "customer"]}
                    placeholder="No external client"
                  />
                ) : project.clientCompany ? (
                  <a
                    href={`/companies/${project.clientCompany.id}`}
                    className="text-sm font-medium text-primary hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/companies/${project.clientCompany!.id}`);
                    }}
                  >
                    {project.clientCompany.name}
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <FieldRow label="Manager" value={project.manager?.name} />
            </div>
          </CardContent>
        </Card>

        {/* Save/Cancel buttons (only in edit mode) */}
        {editMode && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !form.name.trim()}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Save className="h-4 w-4" strokeWidth={1.5} />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Right 30% */}
      <div className="space-y-6 lg:col-span-3">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="text-sm font-semibold text-foreground">Dates</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Clock
                  className="mt-0.5 h-4 w-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Created At
                  </div>
                  <div className="text-foreground">
                    {formatDateTime(project.createdAt)}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock
                  className="mt-0.5 h-4 w-4 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Updated At
                  </div>
                  <div className="text-foreground">
                    {formatDateTime(project.updatedAt)}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              Quick Stats
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Tasks</span>
                <span className="font-medium">{project._count.tasks}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Actions</span>
                <span className="font-medium">{project._count.actions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Milestones</span>
                <span className="font-medium">{project._count.milestones}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expenses</span>
                <span className="font-medium">{project._count.expenses}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

